/**
 * Shared chart utilities used by the four bottom-strip combo/line charts:
 *   - RevenueQuoteFunnelChart
 *   - RevenueOnlineOrdersChart
 *   - QuotesOrdersChart
 *   - UnitsQuotedSoldChart
 *
 * Extracted because each component previously held its own copy of
 * `readSeriesColor`, `formatCurrency`, `formatCount`, and `decorateSeries`.
 * Consolidating here means PBI-fidelity changes (palette tweaks, tooltip
 * format, bar shape, line markers) ship to all four charts from one place.
 *
 * Anything visible (palette indices, bar width, marker size, tooltip HTML,
 * line area gradient opacity) is exposed as a named constant so a designer
 * can tune it without spelunking the chart components.
 */

import type { ComboSeries } from "./useReportData";

/* ------------------------------------------------------------------------- *
 * PBI-fidelity visual constants
 * ------------------------------------------------------------------------- */

/** Bar visual constants — matches PBI default lineClusteredColumnCombo.
 *  HSQ-tweak (2026-05-27): wider bars with a more rounded top read as more
 *  polished/premium than the thin 18px / 2px-radius default. */
export const BAR_MAX_WIDTH = 26;
/** Rounded top edges. [tl, tr, br, bl] in px. */
export const BAR_BORDER_RADIUS: [number, number, number, number] = [5, 5, 0, 0];
/** Gap between adjacent category groups. PBI defaults read tighter than
 *  ECharts' default ~20%; 30% leaves room for the line markers without
 *  making the bars feel anemic. Applied via `barCategoryGap` on the FIRST
 *  bar series in the combo (ECharts treats it as a series-level prop and
 *  inherits to siblings). */
export const BAR_CATEGORY_GAP = "30%";

/** Line visual constants. */
export const LINE_WIDTH = 2;
export const LINE_SYMBOL_SIZE = 6;
/** Faint area-fill opacity underneath each line series (PBI sometimes shows
 *  a light fill; we keep it subtle so the line + markers still dominate). */
export const LINE_AREA_OPACITY = 0.08;

/** Axis + legend type sizing. */
export const AXIS_LABEL_FONT_SIZE = 11;
export const LEGEND_FONT_SIZE = 11;

/** Series palette buckets — bars vs. lines, chosen so they read as distinct
 *  pairs in the combo charts. Indices are 1-based into the `--dash-series-N`
 *  CSS variables (1..8). */
export const BAR_PALETTE = [1, 2, 4, 6] as const;
export const LINE_PALETTE_ORANGE = [3, 5, 7, 8] as const;
export const LINE_PALETTE_BLUE = [1, 2, 4, 6] as const;

/* ------------------------------------------------------------------------- *
 * Color helpers
 * ------------------------------------------------------------------------- */

const PBI_FALLBACK_PALETTE = [
  "#118dff", "#12239e", "#e66c37", "#6b007b",
  "#e044a7", "#744ec2", "#d9b300", "#d64550",
];

/** Read `--dash-series-N` from the document root; falls back to the literal
 *  PBI palette so the chart still renders in test environments where
 *  `tokens.css` isn't loaded. */
export function readSeriesColor(idx: number): string {
  const clamped = ((idx - 1) % 8) + 1; // 1..8
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(`--dash-series-${clamped}`)
      .trim();
    if (value) return value;
  }
  return PBI_FALLBACK_PALETTE[clamped - 1];
}

/** Read any `--dash-*` CSS var from the document root at call time (so it
 *  reflects the active light/dark theme). Falls back to `fallback` in test
 *  environments where tokens.css isn't loaded. Charts call this inside their
 *  option `useMemo` (which depends on `theme`) so the value re-resolves when
 *  the theme flips. */
export function cssVar(name: string, fallback: string): string {
  if (typeof window !== "undefined" && typeof document !== "undefined") {
    const value = getComputedStyle(document.documentElement)
      .getPropertyValue(name)
      .trim();
    if (value) return value;
  }
  return fallback;
}

/** Hex `#rrggbb` → `rgba(r, g, b, a)`. Used for line area-fill gradients. */
export function hexToRgba(hex: string, alpha: number): string {
  const clean = hex.replace("#", "");
  if (clean.length !== 6) return `rgba(17, 141, 255, ${alpha})`;
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* ------------------------------------------------------------------------- *
 * Number formatters
 * ------------------------------------------------------------------------- */

/** Full currency, e.g. `$1,234,567`. Used in tooltips when granularity matters. */
export function formatCurrency(value: number): string {
  if (!Number.isFinite(value)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `$${Math.round(value).toLocaleString()}`;
  }
}

/** Abbreviated currency, e.g. `$1.2M`, `$45K`, `$120`. Mirrors PBI axis labels. */
export function formatCurrencyAbbrev(value: number): string {
  if (!Number.isFinite(value)) return "—";
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${Math.round(abs / 1_000)}K`;
  return formatCurrency(value);
}

/** Whole-number count with thousands separators. */
export function formatCount(value: number): string {
  if (!Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US").format(Math.round(value));
}

/* ------------------------------------------------------------------------- *
 * Series decoration
 * ------------------------------------------------------------------------- */

export interface SeriesDecoration {
  seriesIndex: number;
  /** Combo charts: 0 = left currency axis, 1 = right count axis.
   *  Pure line charts always use 0. */
  yAxisIndex: 0 | 1;
  color: string;
}

/**
 * Combo-chart decoration: first bar-type series owns the left (currency) axis;
 * every other series lands on the right (count) axis. Bars cycle through
 * `BAR_PALETTE`; lines cycle through `LINE_PALETTE_ORANGE` (orange-led) so
 * they read distinct from any bar.
 */
export function decorateComboSeries(series: ComboSeries[]): SeriesDecoration[] {
  let primaryBarPicked = false;
  let barRank = 0;
  let lineRank = 0;
  return series.map((s, idx) => {
    const isBar = s.type === "bar";
    let yAxisIndex: 0 | 1;
    if (isBar && !primaryBarPicked) {
      yAxisIndex = 0;
      primaryBarPicked = true;
    } else {
      yAxisIndex = 1;
    }
    let color: string;
    if (isBar) {
      barRank += 1;
      color = readSeriesColor(BAR_PALETTE[(barRank - 1) % BAR_PALETTE.length]);
    } else {
      lineRank += 1;
      color = readSeriesColor(LINE_PALETTE_ORANGE[(lineRank - 1) % LINE_PALETTE_ORANGE.length]);
    }
    return { seriesIndex: idx, yAxisIndex, color };
  });
}

/**
 * Pure line-chart decoration. `palette` is one of LINE_PALETTE_ORANGE or
 * LINE_PALETTE_BLUE so the two bookmark-variant charts can visually
 * differentiate from each other.
 */
export function decorateLineSeries(
  series: ComboSeries[],
  palette: readonly number[] = LINE_PALETTE_ORANGE,
): SeriesDecoration[] {
  return series.map((_, idx) => ({
    seriesIndex: idx,
    yAxisIndex: 0 as const,
    color: readSeriesColor(palette[idx % palette.length]),
  }));
}

/* ------------------------------------------------------------------------- *
 * ECharts option helpers (PBI fidelity)
 * ------------------------------------------------------------------------- */

/**
 * Build the ECharts `series` array for a combo chart. Bars get rounded top
 * edges + max width; lines get a circle marker at every data point, 2px
 * stroke, and a faint area-fill underneath for that subtle PBI gradient.
 */
export function buildComboSeries(
  series: ComboSeries[],
  decorated: SeriesDecoration[],
): any[] {
  let firstBarSeen = false;
  return series.map((s, i) => {
    const meta = decorated[i];
    const isBar = s.type === "bar";
    if (isBar) {
      const isFirstBar = !firstBarSeen;
      firstBarSeen = true;
      return {
        name: s.name,
        type: "bar",
        yAxisIndex: meta.yAxisIndex,
        data: s.data,
        barMaxWidth: BAR_MAX_WIDTH,
        // ECharts inherits barCategoryGap from the first bar series in a combo.
        ...(isFirstBar ? { barCategoryGap: BAR_CATEGORY_GAP } : {}),
        itemStyle: {
          // HSQ-tweak (2026-05-27): subtle top-to-bottom gradient (full
          // colour at the top fading ~30% toward the base) gives the bars
          // depth and a more premium feel than a flat fill.
          color: {
            type: "linear" as const,
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: meta.color },
              { offset: 1, color: hexToRgba(meta.color, 0.55) },
            ],
          },
          borderRadius: BAR_BORDER_RADIUS,
        },
        emphasis: {
          focus: "series" as const,
          itemStyle: { color: meta.color },
        },
      };
    }
    return {
      name: s.name,
      type: "line",
      yAxisIndex: meta.yAxisIndex,
      data: s.data,
      smooth: false,
      symbol: "circle",
      symbolSize: LINE_SYMBOL_SIZE,
      showSymbol: true,
      lineStyle: { width: LINE_WIDTH, color: meta.color },
      // PBI marker style: filled colored circle with a thin white inner ring.
      itemStyle: {
        color: meta.color,
        borderColor: "#ffffff",
        borderWidth: 1,
      },
      areaStyle: { color: hexToRgba(meta.color, LINE_AREA_OPACITY) },
      emphasis: { focus: "series" as const },
    };
  });
}

/**
 * Build the ECharts `series` array for a pure line chart (no bars).
 */
export function buildLineSeries(
  series: ComboSeries[],
  decorated: SeriesDecoration[],
): any[] {
  return series.map((s, i) => {
    const meta = decorated[i];
    return {
      name: s.name,
      type: "line" as const,
      yAxisIndex: 0,
      data: s.data,
      smooth: false,
      symbol: "circle",
      symbolSize: LINE_SYMBOL_SIZE,
      showSymbol: true,
      lineStyle: { width: LINE_WIDTH, color: meta.color },
      // PBI marker style: filled colored circle with a thin white inner ring.
      itemStyle: {
        color: meta.color,
        borderColor: "#ffffff",
        borderWidth: 1,
      },
      areaStyle: { color: hexToRgba(meta.color, LINE_AREA_OPACITY) },
      emphasis: { focus: "series" as const },
    };
  });
}

/**
 * Combo-chart tooltip formatter. Renders one row per series with the series
 * marker, name, and a value formatted per `currencySeriesNames` (currency if
 * the series name is in the set, count otherwise). Returns an HTML string so
 * ECharts can style the body.
 */
export function makeComboTooltipFormatter(currencySeriesNames: Set<string>) {
  return (params: any) => {
    if (!Array.isArray(params) || params.length === 0) return "";
    const header = params[0]?.axisValueLabel ?? params[0]?.axisValue ?? "";
    const rows = params
      .map((p: any) => {
        const isCurrency = currencySeriesNames.has(p.seriesName);
        const raw = typeof p.value === "number" ? p.value : Number(p.value);
        const valueStr = isCurrency
          ? formatCurrencyAbbrev(raw)
          : formatCount(raw);
        return `
          <div style="display:flex;align-items:center;gap:8px;padding:2px 0;font-size:12px;line-height:1.4;">
            <span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${p.color};"></span>
            <span style="flex:1;color:#475569;">${escapeHtml(p.seriesName)}</span>
            <span style="font-variant-numeric:tabular-nums;font-weight:600;color:#0f172a;">${valueStr}</span>
          </div>`;
      })
      .join("");
    return `
      <div style="min-width:180px;">
        <div style="font-size:11px;font-weight:600;color:#0f172a;margin-bottom:6px;">${escapeHtml(String(header))}</div>
        ${rows}
      </div>`;
  };
}

/** Line-only tooltip formatter — every series is a count. */
export function makeCountTooltipFormatter() {
  return (params: any) => {
    if (!Array.isArray(params) || params.length === 0) return "";
    const header = params[0]?.axisValueLabel ?? params[0]?.axisValue ?? "";
    const rows = params
      .map((p: any) => {
        const raw = typeof p.value === "number" ? p.value : Number(p.value);
        return `
          <div style="display:flex;align-items:center;gap:8px;padding:2px 0;font-size:12px;line-height:1.4;">
            <span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${p.color};"></span>
            <span style="flex:1;color:#475569;">${escapeHtml(p.seriesName)}</span>
            <span style="font-variant-numeric:tabular-nums;font-weight:600;color:#0f172a;">${formatCount(raw)}</span>
          </div>`;
      })
      .join("");
    return `
      <div style="min-width:180px;">
        <div style="font-size:11px;font-weight:600;color:#0f172a;margin-bottom:6px;">${escapeHtml(String(header))}</div>
        ${rows}
      </div>`;
  };
}

/** Count tooltip that renders the named series as percentages (one decimal,
 *  "%" suffix) and everything else as counts. Used by the Quotes Issued vs
 *  Won chart so its "% Won" secondary-axis line reads as a percentage. */
export function makeCountAndPercentTooltipFormatter(percentNames: Set<string>) {
  return (params: any) => {
    if (!Array.isArray(params) || params.length === 0) return "";
    const header = params[0]?.axisValueLabel ?? params[0]?.axisValue ?? "";
    const rows = params
      .map((p: any) => {
        const raw = typeof p.value === "number" ? p.value : Number(p.value);
        const valueStr = percentNames.has(p.seriesName)
          ? `${(Number.isFinite(raw) ? raw : 0).toFixed(1)}%`
          : formatCount(raw);
        return `
          <div style="display:flex;align-items:center;gap:8px;padding:2px 0;font-size:12px;line-height:1.4;">
            <span style="display:inline-block;width:9px;height:9px;border-radius:2px;background:${p.color};"></span>
            <span style="flex:1;color:#475569;">${escapeHtml(p.seriesName)}</span>
            <span style="font-variant-numeric:tabular-nums;font-weight:600;color:#0f172a;">${valueStr}</span>
          </div>`;
      })
      .join("");
    return `
      <div style="min-width:180px;">
        <div style="font-size:11px;font-weight:600;color:#0f172a;margin-bottom:6px;">${escapeHtml(String(header))}</div>
        ${rows}
      </div>`;
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Shared tooltip block (PBI-style: white bg, rounded corners, subtle shadow,
 * dashed axis pointer). Caller supplies the `formatter`.
 */
export function makeTooltipBlock(formatter: (p: any) => string) {
  return {
    trigger: "axis" as const,
    axisPointer: {
      // PBI shows a faint solid vertical guide, not a dashed one. We pin the
      // color to the `--dash-chart-axis-pointer` token (hairline @ .5 alpha)
      // and use the literal rgba as fallback for non-DOM test environments.
      type: "line" as const,
      lineStyle: {
        color:
          (typeof document !== "undefined" &&
            getComputedStyle(document.documentElement)
              .getPropertyValue("--dash-chart-axis-pointer")
              .trim()) ||
          "rgba(148, 163, 184, 0.5)",
        type: "solid" as const,
        width: 1,
        opacity: 1,
      },
    },
    backgroundColor: "#ffffff",
    borderColor: "#e4e6ea",
    borderWidth: 1,
    padding: 10,
    extraCssText:
      "box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10); border-radius: 8px;",
    formatter,
  };
}

/**
 * Standard legend block for all four charts — bottom, horizontal, 11px muted.
 */
export const STANDARD_LEGEND = {
  bottom: 0,
  left: "center" as const,
  orient: "horizontal" as const,
  textStyle: {
    fontSize: LEGEND_FONT_SIZE,
    color: "#475569",
  },
  itemWidth: 10,
  itemHeight: 10,
  itemGap: 16,
};

/**
 * Consistent grid margins so all four charts' y-axis labels visually line up
 * across the 2x2 layout. Uses `containLabel: true` so axis-name labels never
 * clip — the literal `left/right` then act as outer margins (PBI-tight).
 */
export const STANDARD_GRID = {
  left: 12,
  right: 12,
  top: 24,
  bottom: 48,
  containLabel: true,
};

/** Grid for pure line charts (single y-axis, less right padding needed). */
export const SINGLE_AXIS_GRID = {
  left: 12,
  right: 16,
  top: 24,
  bottom: 48,
  containLabel: true,
};
